import {
  CpuChipIcon,
  ServerStackIcon,
  ArrowPathIcon,
  CloudArrowUpIcon,
  ChartBarIcon,
  UsersIcon,
  ArrowTrendingUpIcon,
  BoltIcon,
  ClockIcon,
  CubeIcon,
  Cog6ToothIcon,
  ServerIcon,
  CodeBracketIcon,
  BeakerIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline';
import { Brain } from 'lucide-react';
import CountUp from 'react-countup';
import { motion } from 'framer-motion';
import { AiAndDataSEO } from '../seo/seo';

function HeroSection() {
  return (
    <section className="relative overflow-hidden px-6 pb-32 pt-40">
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        {/* Radial glow */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(139,92,246,0.18),transparent_60%)]" />
        {/* Grid */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(139,92,246,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(139,92,246,0.08)_1px,transparent_1px)] bg-[size:64px_64px]" />
      </div>

      <div className="mx-auto max-w-6xl text-left text-gray-800">
        {/* Caption */}
        <span className="animate-fadeUp mb-4 block text-sm font-medium tracking-wide text-purple-500">
          Production-grade AI systems
        </span>

        <h1 className="mb-8 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
          <span className="animate-fadeUp block dark:text-gray-100">AI & Data Engineering</span>
          <span className="animate-fadeUp animate-gradientFlow mt-2 block bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent delay-150">
            Built for Production
          </span>
        </h1>

        <p className="animate-fadeUp max-w-2xl text-base leading-relaxed delay-300 sm:text-lg dark:text-gray-100/90">
          We design and deploy AI systems that integrate with real products, handle real data, and
          hold up under real-world scale.
        </p>
      </div>
    </section>
  );
}

function CapabilitiesSection() {
  const capabilities = [
    {
      title: 'Data Engineering & Pipelines',
      desc: 'Designing robust data pipelines for ETL, transformation, and ingestion. Optimized for real-time AI systems and scalable data workflows.',
      icon: ServerStackIcon,
      accent: 'from-indigo-400/30 to-purple-400/10',
    },
    {
      title: 'Model Development & Training',
      desc: 'Custom AI and ML model development, including supervised, unsupervised, and deep learning, trained on production-scale datasets.',
      icon: CpuChipIcon,
      accent: 'from-purple-400/30 to-pink-400/10',
    },
    {
      title: 'MLOps & Deployment',
      desc: 'Seamless deployment pipelines, versioning, monitoring, and automated retraining to keep AI models production-ready and reliable.',
      icon: ArrowPathIcon,
      accent: 'from-pink-400/30 to-indigo-400/10',
    },
    {
      title: 'AI Integration',
      desc: 'Embedding AI into applications, APIs, and dashboards to provide real-time decision intelligence and automation.',
      icon: CloudArrowUpIcon,
      accent: 'from-indigo-400/30 to-blue-400/10',
    },
    {
      title: 'Analytics & Decision Systems',
      desc: 'Transforming raw data into actionable insights using predictive analytics, reporting, and visualization for strategic decisions.',
      icon: ChartBarIcon,
      accent: 'from-blue-400/30 to-purple-400/10',
    },
    {
      title: 'Consulting & Team Training',
      desc: 'We provide expert AI consulting and hands-on team training to ensure your organization can adopt AI workflows efficiently and sustainably.',
      icon: UsersIcon,
      accent: 'from-green-400/30 to-teal-400/10',
    },
  ];

  return (
    <section className="relative px-6 py-28">
      {/* Decorative background lines */}
      <div className="absolute inset-0 -z-10">
        <span className="ai-blob blob-indigo/10" />
        <span className="ai-blob blob-purple/10" />
      </div>

      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <motion.div
          className="mb-16 max-w-2xl text-right md:text-left"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <motion.h2
            className="animate-fadeUp animate-gradientFlow mt-2 block bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-4xl font-extrabold leading-tight tracking-tight text-transparent delay-150 sm:text-5xl"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Capabilities
          </motion.h2>
          <motion.p
            className="mt-4 text-lg leading-relaxed text-gray-700 sm:text-xl dark:text-gray-100/90"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
          >
            At Akwid Labs, we engineer production-ready AI & Data systems that convert raw data into
            actionable intelligence. Every capability is carefully designed for scalability,
            reliability, and real-world impact.
          </motion.p>
        </motion.div>

        {/* Capabilities grid */}
        <div className="gap-8 space-y-8 md:grid md:grid-cols-2 md:space-y-0 lg:grid-cols-3">
          {capabilities.map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.title}
                className="group relative overflow-hidden rounded-2xl border border-white/30 bg-white/40 p-6 backdrop-blur-xl"
                initial={{ opacity: 0, y: 50, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                whileHover={{
                  scale: 1.05,
                  y: -5,
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  transition: { duration: 0.2 },
                }}
                transition={{
                  duration: 0.5,
                  delay: i * 0.1,
                  type: 'spring',
                  stiffness: 100,
                }}
                viewport={{ once: true }}
              >
                {/* Accent gradient layer */}
                <motion.div
                  className={`pointer-events-none absolute inset-0 bg-gradient-to-r ${item.accent} rounded-2xl opacity-0`}
                  whileHover={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                />

                {/* Content */}
                <div className="relative z-10 flex h-full flex-col">
                  <motion.div
                    whileHover={{
                      scale: 1.2,
                      rotate: 360,
                      transition: { duration: 0.5 },
                    }}
                    className="mb-4"
                  >
                    <Icon className="h-10 w-10 text-purple-600" />
                  </motion.div>

                  <motion.h3
                    className="text-xl font-semibold text-gray-800"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: i * 0.1 + 0.3 }}
                    viewport={{ once: true }}
                  >
                    {item.title}
                  </motion.h3>

                  <motion.p
                    className="mt-2 text-sm text-gray-600 sm:text-base dark:text-gray-100/90"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: i * 0.1 + 0.4 }}
                    viewport={{ once: true }}
                  >
                    {item.desc}
                  </motion.p>
                </div>

                {/* Decorative line for flow */}
                {i < capabilities.length - 1 && (
                  <motion.span
                    className="absolute right-[-16px] top-1/2 hidden h-0.5 w-16 bg-gradient-to-r from-purple-400 via-indigo-400 to-pink-400 md:block"
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    transition={{ duration: 0.8, delay: i * 0.1 + 0.6 }}
                    viewport={{ once: true }}
                  >
                    <motion.div
                      className="h-full w-full bg-gradient-to-r from-purple-400 via-indigo-400 to-pink-400"
                      animate={{
                        opacity: [0.5, 1, 0.5],
                        scaleX: [1, 1.1, 1],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        delay: i * 0.3,
                      }}
                    />
                  </motion.span>
                )}

                {/* Floating decorative elements */}
                <motion.div
                  className="absolute -right-2 -top-2 h-4 w-4 rounded-full bg-gradient-to-r from-purple-400 to-pink-400 opacity-20"
                  animate={{
                    y: [0, -5, 0],
                    scale: [1, 1.1, 1],
                    opacity: [0.2, 0.4, 0.2],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    delay: i * 0.2,
                  }}
                  whileHover={{
                    scale: 1.5,
                    opacity: 0.6,
                    transition: { duration: 0.2 },
                  }}
                />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function SectorImpactSplitSection() {
  const stats = [
    {
      title: 'Global AI Adoption',
      value: 80,
      unit: '% of Enterprises',
      icon: ArrowTrendingUpIcon,
      color: 'from-purple-400 via-indigo-400 to-pink-400',
    },
    {
      title: 'Data Growth',
      value: 175,
      unit: 'ZB by 2025',
      icon: BoltIcon,
      color: 'from-blue-400 via-cyan-400 to-teal-400',
    },
    {
      title: 'Time-to-Impact',
      value: 6,
      unit: 'Weeks Average',
      icon: ClockIcon,
      color: 'from-pink-400 via-purple-400 to-indigo-400',
    },
  ];

  return (
    <section className="relative mx-auto max-w-6xl px-6 py-28">
      {/* Full-width background */}
      <div className="absolute inset-0 -z-10">
        {/* Radial glow */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(139,92,246,0.18),transparent_60%)]" />
        {/* Grid overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(139,92,246,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(139,92,246,0.08)_1px,transparent_1px)] bg-[size:64px_64px]" />
      </div>

      <div className="mx-auto flex max-w-6xl flex-col items-start gap-12 px-6 lg:flex-row lg:items-center">
        {/* Left Side: Stats */}
        <div className="space-y-8 lg:w-3/5">
          <h2 className="text-4xl font-extrabold leading-tight text-gray-900 sm:text-5xl dark:text-gray-100">
            The World is Adopting{' '}
            <span className="animate-gradientShift bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">
              AI & Data
            </span>{' '}
            Faster Than Ever
          </h2>
          <p className="mt-4 max-w-xl text-lg text-gray-700 sm:text-xl dark:text-gray-100/90">
            Businesses, governments, and organizations are integrating AI at an unprecedented speed.
            Akwid Labs helps you harness this change to stay ahead and drive measurable impact.
          </p>

          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
            {stats.map((item, i) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="group relative flex flex-col items-start overflow-hidden rounded-3xl border border-white/30 bg-white/20 p-6 backdrop-blur-xl transition-transform duration-500 hover:scale-105 hover:shadow-xl"
                >
                  {/* Gradient Accent */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-r ${item.color} rounded-3xl opacity-0 transition-opacity duration-500 group-hover:opacity-30`}
                  />
                  <div className="relative z-10 mb-2 flex items-center space-x-4">
                    <Icon className="h-10 w-10 text-gray-900" />
                    <h3 className="text-2xl font-bold text-gray-900">
                      <CountUp end={item.value} duration={2} />{' '}
                      <span className="text-base font-medium text-gray-700 dark:text-gray-100">
                        {item.unit}
                      </span>
                    </h3>
                  </div>
                  <p className="text-sm text-gray-900/90 sm:text-base dark:text-gray-100/90">
                    {item.title}
                  </p>{' '}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Side: Interactive Visualization */}
        <div className="relative flex h-[400px] items-center justify-center sm:h-[500px] lg:h-[600px] lg:w-2/5">
          {/* Floating gradient nodes with gentle, organic movement */}
          <div className="animate-scaleFloat absolute h-64 w-64 rounded-full bg-gradient-to-tr from-purple-400 via-pink-400 to-indigo-400 opacity-30" />
          <div className="animate-scaleFloat delay-2000 absolute h-48 w-48 rounded-full bg-gradient-to-br from-blue-400 via-cyan-400 to-teal-400 opacity-40" />
          <div className="animate-scaleFloat delay-4000 absolute h-32 w-32 rounded-full bg-gradient-to-tl from-pink-400 via-purple-400 to-indigo-400 opacity-50" />
          <div className="animate-scaleFloat delay-6000 absolute h-20 w-20 rounded-full bg-gradient-to-tr from-indigo-400 via-purple-400 to-pink-400 opacity-60" />

          {/* Center Brain Icon */}
          <div className="relative z-10 flex items-center justify-center">
            <Brain className="animate-fadeUp h-28 w-28 bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text dark:from-indigo-300 dark:via-purple-300 dark:to-pink-300" />
          </div>
        </div>
      </div>
    </section>
  );
}

function HowWeBuildSection() {
  const PROCESS_STEPS = [
    {
      title: 'Discovery & Research',
      desc: 'Understanding your business, data, and objectives to design the right AI solutions.',
      icon: CubeIcon,
      color: 'from-purple-400 via-indigo-400 to-pink-400',
    },
    {
      title: 'Architecture & Design',
      desc: 'Building scalable, reliable, and maintainable AI and Data systems from the ground up.',
      icon: Cog6ToothIcon,
      color: 'from-blue-400 via-cyan-400 to-teal-400',
    },
    {
      title: 'Development & Integration',
      desc: 'Full-stack AI implementation, integration with your existing platforms, and automated workflows.',
      icon: CodeBracketIcon,
      color: 'from-pink-400 via-purple-400 to-indigo-400',
    },
    {
      title: 'Deployment & Monitoring',
      desc: 'Seamless production rollout with continuous monitoring, logging, and updates.',
      icon: ServerIcon,
      color: 'from-green-400 via-lime-400 to-emerald-400',
    },
    {
      title: 'Validation & Insights',
      desc: 'Analytics, feedback loops, and model evaluation for actionable business insights.',
      icon: BeakerIcon,
      color: 'from-yellow-400 via-orange-400 to-red-400',
    },
  ];

  return (
    <section className="mx-auto max-w-6xl px-6 py-28">
      <motion.div
        className="mx-auto mb-16 max-w-7xl text-center"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <motion.h2
          className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl dark:text-gray-100"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <span className="animate-gradientShift bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">
            How We Build
          </span>
        </motion.h2>
        <motion.p
          className="mx-auto mt-4 max-w-2xl text-lg text-gray-600 sm:text-xl dark:text-gray-300"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          Our world-class engineering process transforms ideas into production-ready solutions that
          are scalable, reliable, and tailored to your business.
        </motion.p>
      </motion.div>

      <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
        {PROCESS_STEPS.map((step, i) => {
          const Icon = step.icon;
          return (
            <motion.div
              key={step.title}
              className="group relative flex flex-col items-start overflow-hidden rounded-3xl border border-white/20 bg-white/30 p-6 backdrop-blur-xl"
              initial={{ opacity: 0, y: 50, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              whileHover={{
                scale: 1.05,
                y: -5,
                transition: { duration: 0.2 },
              }}
              transition={{
                duration: 0.5,
                delay: i * 0.15,
                type: 'spring',
                stiffness: 100,
              }}
              viewport={{ once: true }}
            >
              {/* Gradient background that appears on hover */}
              <motion.div
                className={`absolute inset-0 bg-gradient-to-r ${step.color} opacity-0`}
                whileHover={{ opacity: 0.1 }}
                transition={{ duration: 0.3 }}
              />

              {/* Animated icon with enhanced interactions */}
              <motion.div
                className={`flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-r ${step.color} mb-4 text-white shadow-md`}
                whileHover={{
                  scale: 1.2,
                  rotate: 360,
                  boxShadow: '0 10px 25px rgba(139, 92, 246, 0.3)',
                }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.5 }}
              >
                <motion.div whileHover={{ rotate: 15 }} transition={{ duration: 0.3 }}>
                  <Icon className="h-6 w-6" />
                </motion.div>
              </motion.div>

              {/* Title with staggered animation */}
              <motion.h3
                className="mb-2 text-lg font-semibold text-gray-900 sm:text-xl dark:text-gray-100"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: i * 0.15 + 0.3 }}
                viewport={{ once: true }}
              >
                <span className="animate-gradientFlow bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                  {step.title}
                </span>
              </motion.h3>

              {/* Description with smooth reveal */}
              <motion.p
                className="text-sm text-gray-700 sm:text-base dark:text-gray-300"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: i * 0.15 + 0.4 }}
                viewport={{ once: true }}
                whileHover={{ color: 'rgb(17, 24, 39)' }}
              >
                {step.desc}
              </motion.p>

              {/* Animated connecting line for visual flow */}
              {i < PROCESS_STEPS.length - 1 && (
                <motion.div
                  className="absolute -right-4 top-1/2 hidden h-0.5 w-8 transform bg-gradient-to-r from-purple-400 to-transparent opacity-30 lg:block"
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  transition={{ duration: 0.8, delay: i * 0.15 + 0.6 }}
                  viewport={{ once: true }}
                >
                  <motion.div
                    className="h-full w-full bg-gradient-to-r from-purple-400 to-transparent"
                    animate={{
                      opacity: [0.3, 0.8, 0.3],
                      scaleX: [1, 1.1, 1],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: i * 0.5,
                    }}
                  />
                </motion.div>
              )}

              {/* Decorative floating elements with physics */}
              <motion.div
                className="absolute -right-2 -top-2 h-4 w-4 rounded-full bg-gradient-to-r from-purple-400 to-pink-400 opacity-20"
                animate={{
                  y: [0, -5, 0],
                  scale: [1, 1.1, 1],
                  opacity: [0.2, 0.4, 0.2],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  delay: i * 0.3,
                }}
                whileHover={{
                  scale: 1.5,
                  opacity: 0.6,
                  transition: { duration: 0.2 },
                }}
              />
              <motion.div
                className="absolute -bottom-1 -left-1 h-3 w-3 rounded-full bg-gradient-to-r from-indigo-400 to-purple-400 opacity-20"
                animate={{
                  x: [0, 3, 0],
                  scale: [1, 1.2, 1],
                  opacity: [0.2, 0.5, 0.2],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  delay: i * 0.4 + 1,
                }}
                whileHover={{
                  scale: 1.5,
                  opacity: 0.6,
                  transition: { duration: 0.2 },
                }}
              />
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

function ToolingStackStatic() {
  const TOOLS_STATIC = [
    { name: 'Python', icon: '/Images/Logos-Stacks/Python-logo.png' },
    { name: 'Node.js', icon: '/Images/Logos-Stacks/2560px-Node.js_logo.svg.png' },
    { name: 'TensorFlow', icon: '/Images/Logos-Stacks/Logo_Google_Analytics.svg.png' },
    { name: 'PyTorch', icon: '/Images/Logos-Stacks/pytorch.webp' },
    { name: 'Scikit-Learn', icon: '/Images/Logos-Stacks/Scikit_learn.svg.png' },
    { name: 'OpenAI', icon: '/Images/Logos-Stacks/open-ai-logo.png' },
    { name: 'Docker', icon: '/Images/Logos-Stacks/docker.svg' },
    { name: 'Kubernetes', icon: '/Images/Logos-Stacks/kubernetes.png' },
    { name: 'Azure', icon: '/Images/Logos-Stacks/Microsoft_Azure.svg.png' },
    { name: 'PostgreSQL', icon: '/Images/Logos-Stacks/Postgresql_elephant.png' },
    { name: 'MongoDB', icon: '/Images/Logos-Stacks/mongo.png' },
  ];
  return (
    <section className="relative mx-auto w-full max-w-6xl px-6 py-16 sm:px-16">
      <motion.div
        className="mx-auto max-w-6xl rounded-3xl border border-white/20 bg-gradient-to-r from-purple-900/30 via-blue-400/40 to-pink-900/30 p-10 shadow-lg shadow-purple-500/20 backdrop-blur-xl sm:p-16"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        {/* Heading */}
        <motion.div
          className="mx-auto mb-12 max-w-6xl text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <h3 className="animate-gradientFlow animate-fadeUp mt-2 bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-2xl font-extrabold leading-tight tracking-tight text-transparent delay-150 sm:text-3xl">
            Tooling & Stack
          </h3>
          <p className="mx-auto mt-4 max-w-2xl text-base text-gray-600 sm:text-lg dark:text-gray-300">
            We pick the right tools for your problem — scalable, production-ready, and modern.
          </p>
        </motion.div>

        {/* Icons */}
        <motion.div
          className="mx-auto flex flex-wrap items-center justify-center gap-8 px-6 sm:gap-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          {TOOLS_STATIC.map((tool, index) => (
            <motion.div
              key={tool.name}
              className="flex flex-col items-center text-center"
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              whileHover={{
                scale: 1.1,
                y: -5,
                transition: { duration: 0.2 },
              }}
              transition={{
                duration: 0.5,
                delay: 0.6 + index * 0.1,
                type: 'spring',
                stiffness: 100,
              }}
              viewport={{ once: true }}
            >
              <motion.img
                src={tool.icon}
                alt={tool.name}
                className="mb-2 h-10 object-contain"
                whileHover={{ rotate: [0, -10, 10, 0] }}
                transition={{ duration: 0.5 }}
              />
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          className="flex flex-col items-center text-center text-gray-500 dark:text-gray-400"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 1.2 }}
          viewport={{ once: true }}
        >
          + Many More
        </motion.div>
      </motion.div>
    </section>
  );
}

function OutcomesSection() {
  const RESULTS = [
    {
      title: 'Faster Decision Making',
      value: '3x',
      desc: 'Accelerated insights for teams to act immediately',
      icon: RocketLaunchIcon,
      color: 'from-purple-400 via-indigo-400 to-pink-400',
    },
    {
      title: 'Data-Driven Growth',
      value: '75%',
      desc: 'Businesses using AI see measurable improvements in revenue',
      icon: ChartBarIcon,
      color: 'from-blue-400 via-cyan-400 to-teal-400',
    },
    {
      title: 'Operational Efficiency',
      value: '50%',
      desc: 'Reduced time and cost across processes with AI',
      icon: Cog6ToothIcon,
      color: 'from-pink-400 via-purple-400 to-indigo-400',
    },
  ];

  return (
    <section className="relative mx-auto mt-16 px-6 py-28">
      {/* Background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(139,92,246,0.12),transparent_60%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(139,92,246,0.06)_1px,transparent_1px),linear-gradient(to_bottom,rgba(139,92,246,0.06)_1px,transparent_1px)] bg-[size:64px_64px]" />
      </div>

      <motion.div
        className="mx-auto mb-16 max-w-7xl px-4 text-center"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <h2 className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl dark:text-gray-100">
          <span className="animate-gradientShift bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
            Outcomes & Results
          </span>
        </h2>
        <p className="mx-auto mt-4 max-w-3xl text-base leading-relaxed text-gray-600 sm:text-lg dark:text-gray-300">
          Our AI & Data solutions drive measurable impact — improving efficiency, boosting growth,
          and empowering smarter decisions for businesses worldwide.
        </p>
      </motion.div>

      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
        {RESULTS.map((item, i) => {
          const Icon = item.icon;
          return (
            <motion.div
              key={item.title}
              className="group relative flex flex-col items-start overflow-hidden rounded-3xl border border-white/20 bg-white/20 p-6 backdrop-blur-xl"
              initial={{ opacity: 0, y: 50, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              whileHover={{
                scale: 1.05,
                y: -5,
                transition: { duration: 0.2 },
              }}
              transition={{
                duration: 0.5,
                delay: i * 0.15,
                type: 'spring',
                stiffness: 100,
              }}
              viewport={{ once: true }}
            >
              {/* Gradient background that appears on hover */}
              <motion.div
                className={`absolute inset-0 bg-gradient-to-r ${item.color} rounded-3xl opacity-0`}
                whileHover={{ opacity: 0.1 }}
                transition={{ duration: 0.3 }}
              />

              <motion.div
                className={`flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-r ${item.color} mb-4 text-white`}
                whileHover={{ rotate: 360, scale: 1.1 }}
                transition={{ duration: 0.5 }}
              >
                <Icon className="h-6 w-6" />
              </motion.div>

              <motion.h3
                className="mb-1 text-2xl font-bold text-gray-900 sm:text-3xl dark:text-gray-100"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: i * 0.15 + 0.2 }}
              >
                <CountUp end={parseInt(item.value)} duration={2} />
                <span>{item.value.replace(/\d+/g, '')}</span>
              </motion.h3>

              <motion.p
                className="text-sm text-gray-700 sm:text-base dark:text-gray-200"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: i * 0.15 + 0.3 }}
              >
                {item.title}
              </motion.p>

              <motion.p
                className="mt-1 text-sm text-gray-600 dark:text-gray-300"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: i * 0.15 + 0.4 }}
              >
                {item.desc}
              </motion.p>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

function ClientProductsSection() {
  const products = [
    {
      name: 'RushReports',
      description:
        'RushReports delivers real-time AI-powered legislative updates for JAAR Law, automating manual workflows.',
      color: 'from-purple-400 via-purple-500 to-indigo-500',
      image: '/Images/Projects/rushreports.png',
      link: 'https://www.jaarlaw.com/rushreports',
    },
    {
      name: 'UpResponse.io',
      description:
        'UpResponse.io is an AI-driven automated IT support platform, reducing costs and increasing efficiency for businesses.',
      color: 'from-blue-400 via-blue-500 to-cyan-500',
      image: '/Images/Projects/UpRepsonse.png',
      link: 'https://upresponse.io',
    },
  ];

  return (
    <section className="mx-auto max-w-6xl px-6 py-28">
      {/* Section header */}
      <div className="mx-auto mb-16 max-w-7xl px-4 text-center">
        <h2 className="text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl dark:text-gray-100">
          <span className="animate-gradientShift bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
            Products We've Built for Our Clients
          </span>
        </h2>
        <p className="mx-auto mt-4 max-w-3xl text-base leading-relaxed text-gray-600 sm:text-lg dark:text-gray-300">
          These are some of the AI & Data-powered solutions we have delivered, transforming
          workflows and delivering real-world value for our clients.
        </p>
      </div>

      {/* Products grid */}
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 lg:grid-cols-2">
        {products.map((product) => (
          <div
            key={product.name}
            className={`relative flex flex-col overflow-hidden rounded-3xl border border-white/20 bg-gradient-to-tr ${product.color} group p-6 shadow-lg backdrop-blur-xl transition-all duration-500 hover:scale-105`}
          >
            {/* Screenshot or device mockup with float animation */}
            <div className="animate-scaleFloat relative mb-6 flex items-center justify-center overflow-hidden rounded-xl bg-white p-4">
              <img
                src={product.image}
                alt={`${product.name} Screenshot`}
                className="h-56 w-auto rounded-xl object-contain"
              />
            </div>

            {/* Product info */}
            <h3 className="mb-2 text-2xl font-bold text-white">{product.name}</h3>
            <p className="text-sm leading-relaxed text-white/95 sm:text-base">
              {product.description}
            </p>

            {/* Hover button */}
            <a
              href={product.link}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-white/25 px-6 py-2 font-medium text-white opacity-0 backdrop-blur-lg transition-opacity duration-300 hover:bg-white/40 group-hover:opacity-100"
            >
              Learn More
            </a>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function AiAndData() {

  return (
    <div className="mx-auto sm:px-6 lg:px-8">
      <AiAndDataSEO />
      <HeroSection />
      <CapabilitiesSection />
      <SectorImpactSplitSection />
      <HowWeBuildSection />
      <ToolingStackStatic />
      <OutcomesSection />
      <ClientProductsSection />
    </div>
  );
}
